import React, { memo, useEffect, useRef, useCallback, Fragment } from "react";
import * as d3 from "d3";
import "./HawkeyeV2DataDownload.scss";

import { useAxiomState } from "__AxiomComponents/AxiomHooks/AxiomHooks";
import HawkeyeV2Selection from "../HawkeyeV2Selection/HawkeyeV2Selection";
import HawkeyeV2FieldSelection from "./HawkeyeV2FieldSelection";

const formatFromDate = d3.timeFormat("%Y-%m-%d 00:00:00");
const formatToDate = d3.timeFormat("%Y-%m-%d 23:59:59");

function HawkeyeV2DataDownload(props) {
  const [state, dispatch] = useAxiomState(
    {
      selectedViews: [],
      currentSelectedView: [],
      currentSelectedViewColumns: [],
      currentSelectedViewToDate: null,
      currentSelectedViewFromDate: null,
      currentSelectedViewGranularityColumn: null,
    },
    props.appName,
    props.appID,
    "HawkeyeV2DataDownload",
    props.componentID
  );

  const {
    downloadOptions,
    onDownloadData,
    closeDownloadData,
    KPIWiseFilters,
    KPIWiseLocalFilters,
    userDefinedSettings,
    onSaveUserDefinedSettings,
  } = props;

  // Stop scrolling the overall page when modal is open
  useEffect(() => {
    document.body.classList.add("no-scroll");
    return () => {
      document.body.classList.remove("no-scroll");
    };
  }, []);

  // Select all tables and all columns if no selection is made initially
  // and no saved settings are available
  useEffect(() => {
    if (downloadOptions.length > 0) {
      const isUserDownloadSettingsSaved =
        userDefinedSettings && userDefinedSettings.selectedDownloadDetails;
      dispatch((state) => {
        if (
          state.selectedViews.length === 0 &&
          state.currentSelectedView.length === 0 &&
          downloadOptions.length !== 0 &&
          !isUserDownloadSettingsSaved
        ) {
          const selectedViews = downloadOptions.map((d) => {
            const defaultGranularityColumn = Object.keys(d.metadata.minMaxDates)[0];
            return {
              viewID: d.key,
              tableName: d.metadata.tableName,
              selectedColumns: d.metadata.columns,
              selectedDate: {
                columnName: defaultGranularityColumn,
                fromDate: d.metadata.minMaxDates[defaultGranularityColumn].minDate,
                toDate: d.metadata.minMaxDates[defaultGranularityColumn].maxDate,
              },
            };
          });
          return {
            selectedViews,
          };
        }
      });
    }
  }, [dispatch, downloadOptions, userDefinedSettings]);

  // Set initial settings from user defined settings if available
  useEffect(() => {
    dispatch((state) => {
      if (
        state.selectedViews.length === 0 &&
        userDefinedSettings &&
        userDefinedSettings.selectedDownloadDetails
      ) {
        const selectedViews = userDefinedSettings.selectedDownloadDetails.selectedViews;
        if (selectedViews.length > 0) {
          return {
            selectedViews,
          };
        } else {
          return null;
        }
      }
    });
  }, [dispatch, userDefinedSettings]);

  // Set selected date range based on any change in KPIWiseLocalFilters granularity range
  useEffect(() => {
    if (
      KPIWiseLocalFilters &&
      KPIWiseLocalFilters.granularityStartDate &&
      KPIWiseLocalFilters.granularityEndDate
    ) {
      dispatch({
        currentSelectedViewFromDate: new Date(KPIWiseLocalFilters.granularityStartDate),
        currentSelectedViewToDate: new Date(KPIWiseLocalFilters.granularityEndDate),
      });
    }
  }, [dispatch, KPIWiseLocalFilters]);

  const updateFromDate = useCallback(
    (date) => {
      dispatch({
        currentSelectedViewFromDate: date,
      });
    },
    [dispatch]
  );

  const updateToDate = useCallback(
    (date) => {
      dispatch({
        currentSelectedViewToDate: date,
      });
    },
    [dispatch]
  );

  const updateGranularityColumn = useCallback(
    (name, column) => {
      dispatch({
        currentSelectedViewGranularityColumn: column,
      });
    },
    [dispatch]
  );

  const onViewSelection = useCallback(
    (selectedView) => {
      dispatch((state) => {
        const localSelectedView = { ...selectedView };
        let currentSelectedViewColumns = [];
        let currentSelectedViewToDate = null;
        let currentSelectedViewFromDate = null;
        let currentSelectedViewGranularityColumn = null;
        const currentViewIndex = state.selectedViews.findIndex(
          (s) => s.viewID === localSelectedView.key
        );
        if (currentViewIndex !== -1) {
          const currentView = state.selectedViews[currentViewIndex];
          currentSelectedViewColumns = [...currentView.selectedColumns];
          currentSelectedViewToDate = new Date(currentView.selectedDate.toDate);
          currentSelectedViewFromDate = new Date(currentView.selectedDate.fromDate);
          currentSelectedViewGranularityColumn = currentView.selectedDate.columnName;
        } else {
          const granularityColumn = Object.keys(localSelectedView.metadata.minMaxDates)[0];
          currentSelectedViewGranularityColumn = granularityColumn;
          currentSelectedViewFromDate = new Date(
            localSelectedView.metadata.minMaxDates[granularityColumn].minDate
          );
          currentSelectedViewToDate = new Date(
            localSelectedView.metadata.minMaxDates[granularityColumn].maxDate
          );
        }
        if (
          KPIWiseLocalFilters &&
          KPIWiseLocalFilters.granularityStartDate &&
          KPIWiseLocalFilters.granularityEndDate
        ) {
          currentSelectedViewFromDate = new Date(KPIWiseLocalFilters.granularityStartDate);
          currentSelectedViewToDate = new Date(KPIWiseLocalFilters.granularityEndDate);
        }
        return {
          currentSelectedView: [localSelectedView],
          currentSelectedViewColumns,
          currentSelectedViewToDate,
          currentSelectedViewFromDate,
          currentSelectedViewGranularityColumn,
        };
      });
    },
    [dispatch, KPIWiseLocalFilters]
  );

  const onColumnSelection = useCallback(
    (name, values) => {
      dispatch({ currentSelectedViewColumns: values });
    },
    [dispatch]
  );

  const onSelectionCompletion = useCallback(() => {
    dispatch((state) => {
      const selectedViews = [...state.selectedViews];
      const currentSelectedView = state.currentSelectedView[0];
      const selectedColumns = [...state.currentSelectedViewColumns];
      const selectedViewToDate = formatToDate(state.currentSelectedViewToDate);
      const selectedViewFromDate = formatFromDate(state.currentSelectedViewFromDate);
      const selectedViewGranularityColumn = state.currentSelectedViewGranularityColumn;
      const selectedViewDetails = {
        viewID: currentSelectedView.key,
        tableName: currentSelectedView.metadata.tableName,
        selectedColumns: selectedColumns,
        selectedDate: {
          columnName: selectedViewGranularityColumn,
          fromDate: selectedViewFromDate,
          toDate: selectedViewToDate,
        },
      };
      const currentViewIndex = selectedViews.findIndex((s) => s.viewID === currentSelectedView.key);
      if (currentViewIndex === -1) {
        selectedViews.push(selectedViewDetails);
      } else {
        // If user has deselcted all columns from a view remove it from the
        // selected views as it will no longer be downloadable
        if (selectedColumns.length === 0) {
          selectedViews.splice(currentViewIndex, 1);
        } else {
          selectedViews[currentViewIndex] = selectedViewDetails;
        }
      }
      if (selectedViews.length > 0) {
        onSaveUserDefinedSettings({
          ...userDefinedSettings,
          selectedDownloadDetails:
            userDefinedSettings && userDefinedSettings.selectedDownloadDetails
              ? {
                  ...userDefinedSettings.selectedDownloadDetails,
                  selectedViews,
                }
              : {
                  selectedViews,
                },
        });
      }
      return {
        selectedViews,
        currentSelectedView: [],
        currentSelectedViewColumns: [],
        currentSelectedViewToDate: null,
        currentSelectedViewFromDate: null,
        currentSelectedViewGranularityColumn: null,
      };
    });
  }, [dispatch, userDefinedSettings, onSaveUserDefinedSettings]);

  const onDownloadCompletion = useCallback(() => {
    onDownloadData(state.selectedViews, KPIWiseFilters, KPIWiseLocalFilters);
  }, [onDownloadData, state.selectedViews, KPIWiseFilters, KPIWiseLocalFilters]);

  const renderSymbol = useCallback(
    (index, displayName, onSelection, key, isSelected, component) => {
      const currentItemInSelectedViews = state.selectedViews.find((s) => s.viewID === key);
      const selectionIndecator = currentItemInSelectedViews ? (
        <b>({currentItemInSelectedViews.selectedColumns.length} selected)</b>
      ) : null;
      return (
        <div key={index} className="hawkeye-download-selection">
          <button
            key={index}
            title={displayName}
            onClick={onSelection}
            data-id={key}
            className={
              "hawkeye-action hawkeye-selection-item " + (isSelected ? "is-selected " : "")
            }
          >
            {component ? component : `${displayName}`} {selectionIndecator}
          </button>
        </div>
      );
    },
    [state.selectedViews]
  );

  return (
    <div className="hawkeye-filter-modal">
      <div className="hawkeye-filter-modal-content nice-scroll">
        <div className="hawkeye-filter-modal-header">
          <div className="hawkeye-filter-modal-title">{AxiomAPIs.translate("Download data")}</div>
        </div>

        <div className="hawkeye-data-modal-selection">
          <Fragment>
            <HawkeyeV2Selection
              className="hawkeye-filter-create-section hawkeye-filter-create-dimensions"
              isSearchable={true}
              options={downloadOptions}
              selectedOptions={state.currentSelectedView}
              onSelection={onViewSelection}
              appName={props.appName}
              appID={props.appID}
              componentID={props.componentID}
              renderSymbol={renderSymbol}
            />
            <HawkeyeV2FieldSelection
              selectedView={
                state.currentSelectedView.length > 0 ? state.currentSelectedView[0] : null
              }
              selectedColumns={state.currentSelectedViewColumns}
              onColumnSelection={onColumnSelection}
              updateFromDate={updateFromDate}
              updateToDate={updateToDate}
              updateGranularityColumn={updateGranularityColumn}
              onSelectionCompletion={onSelectionCompletion}
              toDate={state.currentSelectedViewToDate}
              fromDate={state.currentSelectedViewFromDate}
              granularityColumn={state.currentSelectedViewGranularityColumn}
              KPIWiseFilters={KPIWiseFilters}
              KPIWiseLocalFilters={KPIWiseLocalFilters}
              appName={props.appName}
              appID={props.appID}
              componentID={props.componentID}
            />
          </Fragment>
        </div>

        <div className="hawkeye-filter-modal-footer">
          <button onClick={closeDownloadData} className="hawkeye-btn hawkeye-btn-secondary">
            {AxiomAPIs.translate("Close")}
          </button>
          <button
            onClick={onDownloadCompletion}
            disabled={state.selectedViews.length === 0}
            className="hawkeye-btn hawkeye-btn-primary"
          >
            {AxiomAPIs.translate("Download")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default memo(HawkeyeV2DataDownload);
